import { format } from 'date-fns';
import { Download, Flag, History, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import type { HostingDeployment, Milestone } from '@/pages/developer/types';

export type PublishedBuildsCardProps = {
  buildGitSha: string | null;
  buildTimeUtc: string | null;

  developerUnlocked: boolean;

  restrictToMilestones?: boolean;

  hostingDeployments: HostingDeployment[];
  hostingDeploymentsLoading: boolean;
  hostingDeploymentsError: string | null;

  repoSnapshotCommitSha: string | null;
  repoSnapshotVersion: string | null;
  repoFilesLoading: boolean;
  repoSnapshotGeneratingCommitSha: string | null;

  milestones: Milestone[];
  openMilestoneEditor: (commitSha: string) => void;

  onViewCommit?: (commitSha: string) => void;

  fetchRepoSnapshot: (input: { commitSha: string; buildTimeUtc: string | null }) => void;
  beginDownloadRepoZip: () => void;
};

export function PublishedBuildsCard({
  buildGitSha,
  buildTimeUtc,
  developerUnlocked,
  restrictToMilestones,
  hostingDeployments,
  hostingDeploymentsLoading,
  hostingDeploymentsError,
  repoSnapshotCommitSha,
  repoSnapshotVersion,
  repoFilesLoading,
  repoSnapshotGeneratingCommitSha,
  milestones,
  openMilestoneEditor,
  onViewCommit,
  fetchRepoSnapshot,
  beginDownloadRepoZip,
}: PublishedBuildsCardProps) {
  const buildSnapshotVersionFromSha = (sha: string) => {
    const trimmed = sha.trim();
    const shortSha = trimmed.slice(0, 7) || 'unknown';
    return `V1.0.${shortSha}`;
  };

  const deploymentsWithMilestones = hostingDeployments.filter((deployment) => {
    const commitSha = (deployment.commitSha ?? '').trim();
    if (!commitSha) return false;
    return milestones.some((milestone) => (milestone.toCommitSha ?? '').trim() === commitSha);
  });

  const displayedDeployments = restrictToMilestones ? deploymentsWithMilestones : hostingDeployments;

  return (
    <Card id="published-builds" className="mb-8">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Published Builds
            </CardTitle>
            <CardDescription>
              {restrictToMilestones
                ? 'Milestone-attached builds.'
                : 'Deployed build history. Milestones highlight verified builds.'}
            </CardDescription>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-1 text-xs text-muted-foreground text-left sm:text-right">
            <span>
              Current: {buildGitSha ? buildGitSha.slice(0, 7) : 'unknown'}
              {buildTimeUtc ? ` · ${buildTimeUtc}` : ''}
            </span>
            <span>
              {deploymentsWithMilestones.length} milestone builds
              {displayedDeployments.length > 0 ? ` (${displayedDeployments.length} total)` : ''}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {hostingDeploymentsError && (
          <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
            {hostingDeploymentsError}
          </div>
        )}

        <div className="rounded-lg border border-border overflow-hidden">
          <div className="sm:hidden">
            {hostingDeploymentsLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Loading deployments…</div>
            ) : displayedDeployments.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {restrictToMilestones
                  ? hostingDeployments.length === 0
                    ? 'No deployments recorded yet. The first visit to a newly deployed build will record it.'
                    : 'No milestone-attached deployments yet. Add a milestone to a published commit to show it here.'
                  : 'No deployments recorded yet. The first visit to a newly deployed build will record it.'}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {displayedDeployments.map((deployment) => {
                  const commitSha = (deployment.commitSha ?? '').trim();
                  const normalizedSnapshotCommitSha = (repoSnapshotCommitSha ?? '').trim();
                  const normalizedGeneratingCommitSha = (repoSnapshotGeneratingCommitSha ?? '').trim();

                  const canDownloadThisCommit = !!normalizedSnapshotCommitSha && normalizedSnapshotCommitSha === commitSha;
                  const alreadyGenerated = canDownloadThisCommit;
                  const isThisCommitGenerating =
                    repoFilesLoading && !!commitSha && !!normalizedGeneratingCommitSha && normalizedGeneratingCommitSha === commitSha;

                  const milestoneForCommit =
                    commitSha.length > 0
                      ? milestones.find((milestone) => (milestone.toCommitSha ?? '').trim() === commitSha) ?? null
                      : null;

                  return (
                    <div key={deployment.id} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium">
                            {Number.isNaN(new Date(deployment.firstSeenAt).getTime())
                              ? '—'
                              : format(new Date(deployment.firstSeenAt), 'MMM d, yyyy · h:mm a')}
                          </p>
                          <div className="mt-1 text-xs text-muted-foreground">
                            <span className="font-mono">
                              {!commitSha ? (
                                '—'
                              ) : onViewCommit ? (
                                <Button
                                  type="button"
                                  variant="link"
                                  size="sm"
                                  className="h-auto px-0 py-0 font-mono text-xs"
                                  title={commitSha}
                                  onClick={() => onViewCommit(commitSha)}
                                >
                                  {commitSha.slice(0, 7)}
                                </Button>
                              ) : (
                                <span title={commitSha}>{commitSha.slice(0, 7)}</span>
                              )}
                            </span>
                            <span> · {milestoneForCommit ? milestoneForCommit.title : '—'}</span>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            Version{' '}
                            <span className="font-mono">
                              {canDownloadThisCommit && repoSnapshotVersion
                                ? repoSnapshotVersion
                                : buildSnapshotVersionFromSha(deployment.commitSha)}
                            </span>
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">Build time (UTC): {deployment.buildTimeUtc ?? '—'}</p>
                          <p className="mt-1 text-xs text-muted-foreground">Origin: {deployment.hostingOrigin ?? '—'}</p>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-col gap-2">
                        {developerUnlocked && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full gap-2"
                            disabled={!commitSha}
                            onClick={() => openMilestoneEditor(commitSha)}
                          >
                            <Flag className="w-4 h-4" />
                            {milestoneForCommit ? 'Edit milestone' : 'Add milestone'}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full gap-2"
                          disabled={repoFilesLoading || alreadyGenerated}
                          onClick={() =>
                            fetchRepoSnapshot({
                              commitSha: deployment.commitSha,
                              buildTimeUtc: deployment.buildTimeUtc,
                            })
                          }
                        >
                          <RefreshCw className={`w-4 h-4 ${isThisCommitGenerating ? 'animate-spin' : ''}`} />
                          {alreadyGenerated ? 'Snapshot generated' : isThisCommitGenerating ? 'Generating…' : 'Generate snapshot'}
                        </Button>
                        <Button
                          variant={canDownloadThisCommit ? 'default' : 'outline'}
                          size="sm"
                          className="w-full gap-2"
                          disabled={!canDownloadThisCommit}
                          onClick={beginDownloadRepoZip}
                        >
                          <Download className="w-4 h-4" />
                          Download ZIP
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <Table className="min-w-[1240px]">
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead>First seen</TableHead>
                  <TableHead>Commit</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Milestone</TableHead>
                  <TableHead>Build time (UTC)</TableHead>
                  <TableHead>Origin</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hostingDeploymentsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-6 text-center text-muted-foreground">
                      Loading deployments…
                    </TableCell>
                  </TableRow>
                ) : displayedDeployments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-6 text-center text-muted-foreground">
                      {restrictToMilestones
                        ? hostingDeployments.length === 0
                          ? 'No deployments recorded yet. The first visit to a newly deployed build will record it.'
                          : 'No milestone-attached deployments yet. Add a milestone to a published commit to show it here.'
                        : 'No deployments recorded yet. The first visit to a newly deployed build will record it.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedDeployments.map((deployment) => {
                    const commitSha = (deployment.commitSha ?? '').trim();
                    const normalizedSnapshotCommitSha = (repoSnapshotCommitSha ?? '').trim();
                    const normalizedGeneratingCommitSha = (repoSnapshotGeneratingCommitSha ?? '').trim();

                    const canDownloadThisCommit = !!normalizedSnapshotCommitSha && normalizedSnapshotCommitSha === commitSha;
                    const alreadyGenerated = canDownloadThisCommit;
                    const isThisCommitGenerating =
                      repoFilesLoading && !!commitSha && !!normalizedGeneratingCommitSha && normalizedGeneratingCommitSha === commitSha;

                    const milestoneForCommit =
                      commitSha.length > 0
                        ? milestones.find((milestone) => (milestone.toCommitSha ?? '').trim() === commitSha) ?? null
                        : null;

                    return (
                      <TableRow key={deployment.id}>
                        <TableCell className="text-sm text-muted-foreground">
                          {Number.isNaN(new Date(deployment.firstSeenAt).getTime())
                            ? '—'
                            : format(new Date(deployment.firstSeenAt), 'MMM d, yyyy · h:mm a')}
                        </TableCell>
                        <TableCell className="text-xs font-mono">
                          {!commitSha ? (
                            '—'
                          ) : onViewCommit ? (
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="h-auto px-0 py-0 font-mono text-xs"
                              title={commitSha}
                              onClick={() => onViewCommit(commitSha)}
                            >
                              {commitSha.slice(0, 7)}
                            </Button>
                          ) : (
                            <span title={commitSha}>{commitSha.slice(0, 7)}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-muted-foreground">
                          {canDownloadThisCommit && repoSnapshotVersion
                            ? repoSnapshotVersion
                            : buildSnapshotVersionFromSha(deployment.commitSha)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {milestoneForCommit ? milestoneForCommit.title : '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{deployment.buildTimeUtc ?? '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{deployment.hostingOrigin ?? '—'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            {developerUnlocked && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                disabled={!commitSha}
                                onClick={() => openMilestoneEditor(commitSha)}
                              >
                                <Flag className="w-4 h-4" />
                                {milestoneForCommit ? 'Edit milestone' : 'Add milestone'}
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-2"
                              disabled={repoFilesLoading || alreadyGenerated}
                              onClick={() =>
                                fetchRepoSnapshot({
                                  commitSha: deployment.commitSha,
                                  buildTimeUtc: deployment.buildTimeUtc,
                                })
                              }
                            >
                              <RefreshCw className={`w-4 h-4 ${isThisCommitGenerating ? 'animate-spin' : ''}`} />
                              {alreadyGenerated ? 'Snapshot generated' : isThisCommitGenerating ? 'Generating…' : 'Generate snapshot'}
                            </Button>
                            <Button
                              variant={canDownloadThisCommit ? 'default' : 'outline'}
                              size="sm"
                              className="gap-2"
                              disabled={!canDownloadThisCommit}
                              onClick={beginDownloadRepoZip}
                            >
                              <Download className="w-4 h-4" />
                              Download ZIP
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <p className="mt-3 text-xs text-muted-foreground">
          If “Download ZIP” is disabled, first click “Generate snapshot” for that commit (the snapshot download is always the current stored sanitized snapshot).
        </p>

        {!developerUnlocked && (
          <p className="mt-1 text-xs text-muted-foreground">Developer passcode required to add or edit milestones.</p>
        )}
      </CardContent>
    </Card>
  );
}
